import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { ThumbDown as ThumbDownIcon, ThumbUp as ThumbUpIcon } from '@mui/icons-material';
import { curveCardinal } from '@visx/curve';
import { Glyph } from '@visx/glyph';
import { AnimatedAxis, Axis, GlyphSeries, Grid, LineSeries, Tooltip, XYChart } from '@visx/xychart';
import moment from 'moment';
import 'moment/locale/fr-ca';
import 'moment/locale/en-ca';
import {
  COLOR_CHATHAMS_BLUE,
  FONT_SIZE_10_PX,
  FONT_SIZE_16_PX,
  FONT_WEIGHT_NORMAL,
} from 'popTheme';
import { selectResultsSelectedDate } from 'redux/reducers/results';
import { selectProfile } from 'redux/reducers/session';
import { ResultCategories, getColorForQuestionnaireType } from './Results';

export const buildFilteredResults = (resultsArray = [], selectedDate, numberOfDataPoints) => {
  // Find the index of the selected date.
  const indexOfSelectedResult = resultsArray.map((result) => result.date).indexOf(selectedDate);
  // Start with the whole array.
  let beginningIndex = 0;
  let endIndex = resultsArray.length - 1;

  // We want the selected date to be in the middle if possible.
  if (indexOfSelectedResult < beginningIndex + numberOfDataPoints / 2) {
    // We need to start at the beginning of the array.  Just calculate where the last one should be.
    endIndex = Math.min(endIndex, numberOfDataPoints - 1);
  } else if (indexOfSelectedResult >= endIndex - numberOfDataPoints / 2) {
    // We need to end at the end of the array.  Just calculate where the first one should be.
    beginningIndex = Math.max(beginningIndex, endIndex + 1 - numberOfDataPoints);
  } else {
    // We are somewhere in the middle of the array.  Just make the selected element in the middle.
    beginningIndex = Math.ceil(indexOfSelectedResult - numberOfDataPoints / 2);
    endIndex = beginningIndex + numberOfDataPoints - 1;
  }
  // Slice goes from the beginning index and up to, but not including the end.
  return resultsArray.slice(beginningIndex, endIndex + 1);
};

export const createDataArrayForKey = (results, key) => {
  return results?.length > 0 && key in results[0]
    ? results.map((result) => {
        return {
          value: Math.max(0, result[key]), // If it's less than 0, just graph as 0.
          date: result.date,
        };
      })
    : [];
};

// This draws the trends graph given the set of results and a selected date.
// The only way to be able to test the XYChart is to allow the tests to provide a width: https://github.com/airbnb/visx/issues/951#issuecomment-785305999
export const TrendsGraph = ({ results, width, isMobile }) => {
  const intl = useIntl();
  const theme = useTheme();
  const selectedDate = useSelector(selectResultsSelectedDate);
  const [filteredResults, setFilteredResults] = useState([]);

  const matches = {
    xs: useMediaQuery(theme.breakpoints.only('xs')),
    sm: useMediaQuery(theme.breakpoints.only('sm')),
    md: useMediaQuery(theme.breakpoints.only('md')),
    lg: useMediaQuery(theme.breakpoints.only('lg')),
  };

  const getTicksCount = useCallback(() => {
    if (matches.xs) return 3;
    if (matches.sm) return 4;
    if (matches.md) return 5;
    if (matches.lg) return 6;
    return 6; // Default value
  }, [matches.xs, matches.sm, matches.md, matches.lg]);

  useEffect(() => {
    // We only want to show about 6 points in the graph.  So the chart will only contain the 6 results around the selected date.
    setFilteredResults(buildFilteredResults(results, selectedDate, getTicksCount()));
  }, [results, selectedDate, getTicksCount]);

  // Custom glyph component
  const CustomGlyph = ({ x, y, datum, color }) => (
    <Glyph left={x} top={y}>
      {datum.date === selectedDate ? (
        <>
          <rect width={'3em'} height={'2em'} rx={'1em'} fill={color} x={'-1.5em'} y={'-1em'} />
          <text
            fontSize={FONT_SIZE_16_PX}
            textAnchor={'middle'}
            dy={'0.4em'}
            fill={COLOR_CHATHAMS_BLUE}
          >
            {datum.value}
          </text>
        </>
      ) : (
        <circle r={'0.5em'} fill={color} />
      )}
    </Glyph>
  );

  const renderThumbIcon = ({ formattedValue }) => {
    const iconProps = {
      width: isMobile ? '0.5625em' : '0.8375em',
      height: isMobile ? '0.5625em' : '0.8375em',
      sx: { color: theme.palette.primary.main },
    };

    switch (formattedValue) {
      case '0':
        return <ThumbUpIcon {...iconProps} data-testid="thumb-up-icon" />;
      case '100':
        return <ThumbDownIcon {...iconProps} data-testid="thumb-down-icon" />;
      default:
        return null;
    }
  };

  const isCategorySelected = () => {
    return (
      !!filteredResults[0] &&
      (!!filteredResults[0][ResultCategories.PAIN] ||
        !!filteredResults[0][ResultCategories.MOOD] ||
        !!filteredResults[0][ResultCategories.SLEEP] ||
        !!filteredResults[0][ResultCategories.WORRY] ||
        isSubstanceSelected())
    );
  };

  // Since substance can be skipped, we need to check every result to see if it's there.
  const isSubstanceSelected = () => {
    let res = false;
    filteredResults.forEach((result) => {
      if (result && result[ResultCategories.SUBSTANCE] !== undefined) {
        res = true;
      }
    });
    return res;
  };

  const PopLineSeries = ({ valueKey, results }) => {
    const currentProfile = useSelector(selectProfile);
    const language = currentProfile?.language;
    // Accessors
    const xAccessor = (result) =>
      result.date
        ? language === 'FRENCH' || intl.locale === 'fr'
          ? moment(result.date).locale('fr-ca').format('MMM DD')
          : moment(result.date).locale('en-ca').format('MMM DD')
        : undefined;
    const yAccessor = (result) => result.value;
    const colorAccessor = (dataKey) => getColorForQuestionnaireType(dataKey);
    const resultsForKey = createDataArrayForKey(results, valueKey);

    return (
      <>
        <LineSeries
          dataKey={valueKey}
          data={resultsForKey}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={colorAccessor}
          curve={curveCardinal}
        />
        <GlyphSeries
          dataKey={'glyph-' + valueKey}
          data={resultsForKey}
          xAccessor={xAccessor}
          yAccessor={yAccessor}
          colorAccessor={() => getColorForQuestionnaireType(valueKey)}
          curve={curveCardinal}
          renderGlyph={CustomGlyph}
        />
      </>
    );
  };

  const TICK_VALUES = [0, 20, 40, 60, 80, 100];

  const yAxisFontProps = {
    fill: theme.palette.primary.main,
    fontSize: isMobile ? FONT_SIZE_10_PX : FONT_SIZE_16_PX,
    fontWeight: FONT_WEIGHT_NORMAL,
  };

  // TODO Doesn't seem to resize well going from wide to more narrow.  Need to add a ResizeObserver?: https://airbnb.io/visx/docs/responsive */
  return (
    <XYChart
      height={480}
      width={width}
      xScale={{ type: 'band' }}
      yScale={{ type: 'linear', domain: [0, 100] }}
      margin={{ top: 25, left: isMobile ? 25 : 50, bottom: 75, right: 25 }}
    >
      <Axis
        orientation="bottom"
        hideTicks={true}
        hideAxisLine={true}
        numTicks={getTicksCount()}
        tickLabelProps={{
          ...yAxisFontProps,
          dy: '1.25em',
        }}
        labelOffset={100}
      />
      <Axis
        orientation="left"
        hideTicks={true}
        hideAxisLine={true}
        tickValues={TICK_VALUES}
        tickLabelProps={{ ...yAxisFontProps }}
      />
      <AnimatedAxis
        orientation="right"
        hideTicks={true}
        hideAxisLine={true}
        tickValues={[0, 100]}
        tickComponent={(formattedValue) => renderThumbIcon(formattedValue)}
        top={isMobile ? -10 : -12}
      />
      <Grid
        columns={false}
        stroke={theme.palette.primary.main}
        lineStyle={{ opacity: 0.2 }}
        strokeDasharray="3"
        strokeWidth={2}
        tickValues={TICK_VALUES}
      />
      {isCategorySelected() ? (
        <>
          <Tooltip
            snapTooltipToDatumX
            snapTooltipToDatumY
            renderTooltip={({ tooltipData }) => {
              const score = tooltipData?.nearestDatum?.datum?.value;
              return (
                <Box
                  style={{
                    fontSize: FONT_SIZE_16_PX,
                    fontWeight: FONT_WEIGHT_NORMAL,
                  }}
                >
                  {score !== undefined && !isNaN(score) && score}
                </Box>
              );
            }}
          />
          <PopLineSeries valueKey={ResultCategories.PAIN} results={filteredResults} />
          <PopLineSeries valueKey={ResultCategories.WORRY} results={filteredResults} />
          <PopLineSeries valueKey={ResultCategories.MOOD} results={filteredResults} />
          <PopLineSeries valueKey={ResultCategories.SLEEP} results={filteredResults} />
          <PopLineSeries valueKey={ResultCategories.SUBSTANCE} results={filteredResults} />
        </>
      ) : (
        // Here we need to create a dummy line series so that visx will render an empty chart.
        <>
          <PopLineSeries
            valueKey={ResultCategories.PAIN}
            results={[{ date: undefined, pain: undefined }]}
          />
        </>
      )}
    </XYChart>
  );
};
